import { useEffect, useRef } from "react";

import { BackspaceIcon, WarningCircleIcon } from "@phosphor-icons/react";

import { PIN_LENGTH } from "safety/components/attendance/format";

const KEY_CLASS =
  "tw:h-[66px] tw:cursor-pointer tw:rounded-sm tw:border tw:border-stroke tw:bg-off-white tw:text-2xl tw:text-midnight tw:active:bg-white tw:disabled:opacity-50";

type PinPadProps = {
  pin: string;
  pinError: string | null;
  disabled: boolean;
  onChange: (value: string) => void;
};

export const PinPad = ({ pin, pinError, disabled, onChange }: PinPadProps): React.JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);

  // Autofocus keyboard entry on devices with a real pointer; a touch device keeps its on-screen
  // keypad and isn't focused, so the OS keyboard doesn't pop up over it.
  useEffect(() => {
    if (window.matchMedia("(pointer: fine)").matches) inputRef.current?.focus();
  }, []);

  const dotClass = (index: number): string => {
    if (pinError) return "tw:border-secondary-clay tw:bg-secondary-clay/10";
    if (index === pin.length) return "tw:border-primary-purple tw:bg-white";
    return "tw:border-stroke tw:bg-white";
  };

  return (
    <div>
      {/* A transparent input overlays the boxes for real focus and native backspace; the boxes render state. */}
      <div className="tw:relative">
        {/* Not disabled during the lookup — disabling blurs it, and the parent already ignores input
            while a lookup is pending, so focus survives a wrong-PIN retry. */}
        <input
          ref={inputRef}
          value={pin}
          inputMode="numeric"
          autoComplete="off"
          maxLength={PIN_LENGTH}
          aria-label="Kiosk PIN"
          className="tw:absolute tw:inset-0 tw:z-10 tw:size-full tw:cursor-pointer tw:opacity-0"
          onChange={(event) => onChange(event.target.value)}
        />
        <div aria-hidden="true" className="tw:flex tw:justify-center tw:gap-2">
          {Array.from({ length: PIN_LENGTH }, (_, index) => (
            <div
              key={index}
              className={`tw:flex tw:size-16 tw:items-center tw:justify-center tw:rounded-sm tw:border tw:text-2xl ${dotClass(index)}`}
            >
              {pin[index] ? "•" : ""}
            </div>
          ))}
        </div>
      </div>
      {pinError && (
        <div className="tw:mt-3 tw:flex tw:items-start tw:justify-center tw:gap-1.5 tw:text-sm tw:text-secondary-clay">
          <WarningCircleIcon size={18} className="tw:mt-0.5 tw:shrink-0" />
          <span>{pinError}</span>
        </div>
      )}
      <div className="tw:mt-6 tw:grid tw:grid-cols-3 tw:gap-2 tw:sm:hidden">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((digit) => (
          <button
            key={digit}
            type="button"
            disabled={disabled}
            className={KEY_CLASS}
            onClick={() => onChange(pin + digit)}
          >
            {digit}
          </button>
        ))}
        <div />
        <button type="button" disabled={disabled} className={KEY_CLASS} onClick={() => onChange(pin + "0")}>
          0
        </button>
        <button
          type="button"
          disabled={disabled}
          aria-label="Delete digit"
          className={`tw:flex tw:items-center tw:justify-center ${KEY_CLASS}`}
          onClick={() => onChange(pin.slice(0, -1))}
        >
          <BackspaceIcon size={26} />
        </button>
      </div>
    </div>
  );
};
